import { createConsumer } from "@rails/actioncable";
import { config } from "./config";

class NotificationService {
  constructor() {
    this.consumer = null;
    this.subscription = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.connectionParams = null;
    this.callbacks = {
      onNotification: [],
      onConnect: [],
      onDisconnect: [],
    };
  }

  // Initialize the WebSocket connection
  connect(userId, accessToken) {
    if (this.isConnected) {
      return;
    }

    // Store connection parameters for reconnection
    this.connectionParams = { userId, accessToken };

    // Get base URL from environment and construct cable endpoint
    const cableUrl = config.websocketUrl;

    this.tryConnect(cableUrl, userId, accessToken);
  }

  tryConnect(cableUrl, userId, accessToken) {
    try {
      // Create consumer with authorization
      this.consumer = createConsumer(`${cableUrl}?token=${accessToken}`);

      // Subscribe to the notifications channel
      this.subscription = this.consumer.subscriptions.create(
        {
          channel: "NotificationsChannel",
          user_id: userId,
        },
        {
          connected: () => {
            this.isConnected = true;
            this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
            this.reconnectDelay = 1000; // Reset delay
            this.callbacks.onConnect.forEach((callback) => callback());
          },

          disconnected: () => {
            this.isConnected = false;
            this.callbacks.onDisconnect.forEach((callback) => callback());

            // Attempt to reconnect if we have connection params
            this.attemptReconnection();
          },

          received: (data) => {
            if (data.type === "new_notification" && data.notification) {
              this.callbacks.onNotification.forEach((callback) =>
                callback(data.notification),
              );
            } else if (data.notification) {
              // Handle direct notification format
              this.callbacks.onNotification.forEach((callback) =>
                callback(data),
              );
            }
          },

          rejected: () => {
            console.error("🚫 Subscription rejected for:", cableUrl);
            this.isConnected = false;
            this.attemptReconnection();
          },
        },
      );
    } catch (error) {
      console.error("💥 Error creating consumer for:", cableUrl, error);
      this.attemptReconnection();
    }
  }

  // Handle automatic reconnection
  attemptReconnection() {
    if (
      !this.connectionParams ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("🔄 Max reconnection attempts reached. Giving up.");
      }
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );

    // Capture connection params before setTimeout to avoid race condition
    const { userId, accessToken } = this.connectionParams;

    setTimeout(() => {
      if (!this.isConnected && this.connectionParams) {
        this.disconnect(); // Clean up any existing connections
        this.connect(userId, accessToken);
      }
    }, delay);
  }

  // Disconnect from the WebSocket
  disconnect() {
    // Clear connection params to prevent reconnection
    this.connectionParams = null;
    this.reconnectAttempts = 0;

    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }

    this.isConnected = false;
  }

  // Set callback functions
  onNotification(callback) {
    this.callbacks.onNotification.push(callback);
  }

  onConnect(callback) {
    this.callbacks.onConnect.push(callback);
  }

  onDisconnect(callback) {
    this.callbacks.onDisconnect.push(callback);
  }

  // Check if connected
  getConnectionStatus() {
    return this.isConnected;
  }

  // Manually trigger reconnection
  reconnect() {
    if (this.connectionParams) {
      this.disconnect();
      setTimeout(() => {
        this.connect(
          this.connectionParams.userId,
          this.connectionParams.accessToken,
        );
      }, 1000);
    } else {
      console.warn("⚠️ No connection parameters available for reconnection");
    }
  }

  // Get reconnection status
  getReconnectionStatus() {
    return {
      attempts: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      hasConnectionParams: !!this.connectionParams,
    };
  }
}

// Export a singleton instance
export const notificationService = new NotificationService();
